<script lang="ts">
	import { onMount } from 'svelte';
	import '@andresot/tokens';
	import MusicHost from '$components/music/MusicHost.svelte';
	import { featureFlags } from '$lib/config/features';
	import { install } from '$stores/install.svelte';
	import { maintenance } from '$stores/maintenance.svelte';
	import { network } from '$stores/network.svelte';
	import { prefs } from '$stores/prefs.svelte';
	import { session } from '$stores/session.svelte';
	import { ui } from '$stores/ui.svelte';
	import InstallScreen from '$components/install/InstallScreen.svelte';
	import MaintenanceScreen from '$components/MaintenanceScreen.svelte';
	import Toaster from '$components/Toaster.svelte';
	import AnnouncementCard from '$components/AnnouncementCard.svelte';
	import DesktopUpdateBanner from '$components/DesktopUpdateBanner.svelte';
	import { initDesktop } from '$lib/desktop.svelte';

	let { children } = $props();

	// Сохранённые тема и размер текста применяются один раз при запуске.
	// До этого момента действует значение из атрибута в app.html, поэтому
	// светлой вспышки при загрузке тёмной темы не происходит.
	onMount(() => {
		prefs.apply();
		const stopDesktop = initDesktop();

		/*
		 * Служебный сценарий регистрируется только в собранной версии.
		 * В разработке он отдавал бы сохранённые копии модулей, и правки
		 * переставали бы доезжать до браузера.
		 */
		if (import.meta.env.PROD && 'serviceWorker' in navigator) {
			/*
			 * Сначала как модуль, при отказе — обычным скриптом. Safari принял
			 * модульные служебные сценарии поздно, а без запасного пути там
			 * не работают ни офлайн, ни уведомления при закрытом приложении.
			 */
			navigator.serviceWorker
				.register('/service-worker.js', { type: 'module' })
				.catch(() => navigator.serviceWorker.register('/service-worker.js'))
				.catch(() => {
					// Совсем без сценария приложение работает, просто
					// не открывается без сети и не принимает уведомления.
				});
		}

		install.init();

		// Токен доступа живёт 15 минут и при перезагрузке теряется, но печенье
		// с токеном обновления остаётся — по нему восстанавливаемся молча,
		// без повторного ввода пароля.
		/*
		 * Технические работы. Подписку на отказы ставим до первого запроса —
		 * иначе самый ранний отказ прошёл бы мимо, и человек увидел бы пустое
		 * приложение вместо объяснения.
		 */
		maintenance.listen();
		void maintenance.refresh();

		void session.restore();

		/*
		 * Третий слой запрета масштабирования. Мета-тег и touch-action
		 * снимают двойное касание, но щипок Safari ими не останавливает —
		 * он приходит отдельными событиями gesture*, и гасить нужно их.
		 *
		 * Гасим для всей страницы: масштаб в iOS общий на документ, и пустить
		 * его «только над фотографией» нельзя — растянулось бы всё окно.
		 * Когда просмотру фото понадобится приближение, он будет двигать
		 * своё изображение сам, а не масштаб страницы.
		 */
		const stopGesture = (event: Event) => event.preventDefault();
		for (const name of ['gesturestart', 'gesturechange', 'gestureend']) {
			document.addEventListener(name, stopGesture, { passive: false });
		}

		/*
		 * `overscroll-behavior: none` не останавливает резину установленной iOS
		 * PWA: Safari двигает весь visual viewport, и фиксированные шапка с
		 * нижним меню уезжают вместе с приложением. Поэтому гасим только жест,
		 * который пытается выйти за верхнюю или нижнюю границу внутреннего
		 * скроллера. Обычная прокрутка внутри списка и горизонтальные свайпы
		 * историй остаются нативными.
		 */
		let touchStartX = 0;
		let touchStartY = 0;
		let lastTouchY = 0;

		const findVerticalScroller = (target: EventTarget | null): HTMLElement | null => {
			let element = target instanceof Element ? target : null;
			while (element && element !== document.documentElement) {
				if (element instanceof HTMLElement) {
					const { overflowY } = getComputedStyle(element);
					if (
						(overflowY === 'auto' || overflowY === 'scroll') &&
						element.scrollHeight > element.clientHeight + 1
					) {
						return element;
					}
				}
				element = element.parentElement;
			}
			/*
			 * Внутреннего блока нет — значит, прокручивается сама страница: так
			 * устроены документы (соглашение, политика, «Помощь»). Раньше здесь
			 * возвращался null, и ниже гасилось каждое движение пальца — документ
			 * на телефоне почти не листался. Корень считается обычным скроллером:
			 * в середине жест нативный, а на краях по-прежнему гасится, и резина
			 * установленного приложения не возвращается.
			 */
			const root = document.scrollingElement;
			if (root instanceof HTMLElement && root.scrollHeight > root.clientHeight + 1) return root;
			return null;
		};

		const rememberTouch = (event: TouchEvent): void => {
			if (event.touches.length !== 1) return;
			const touch = event.touches[0];
			if (!touch) return;
			touchStartX = touch.clientX;
			touchStartY = touch.clientY;
			lastTouchY = touch.clientY;
		};

		const stopRubberBand = (event: TouchEvent): void => {
			if (!ui.isMobile || event.touches.length !== 1) return;
			const touch = event.touches[0];
			if (!touch) return;
			const deltaY = touch.clientY - lastTouchY;
			const totalX = Math.abs(touch.clientX - touchStartX);
			const totalY = Math.abs(touch.clientY - touchStartY);
			lastTouchY = touch.clientY;

			// Горизонтальная лента историй не должна потерять собственный свайп.
			if (totalY <= totalX || deltaY === 0) return;

			const scroller = findVerticalScroller(event.target);
			if (!scroller) {
				event.preventDefault();
				return;
			}

			const atTop = scroller.scrollTop <= 0;
			const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
			if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) event.preventDefault();
		};

		document.addEventListener('touchstart', rememberTouch, { passive: true });
		document.addEventListener('touchmove', stopRubberBand, { passive: false });

		/*
		 * Файл, брошенный мимо чата, браузер открывает вместо приложения: вкладка
		 * уходит на этот файл, а идущий звонок обрывается. Здесь такое гасится.
		 * Области, которые файлы принимают, отменяют событие сами — до окна оно
		 * доходит уже отменённым, и мы его не трогаем.
		 */
		const refuseStrayFile = (event: DragEvent): void => {
			if (event.defaultPrevented || !event.dataTransfer?.types.includes('Files')) return;
			if (event.target instanceof HTMLInputElement && event.target.type === 'file') return;
			event.preventDefault();
			if (event.type === 'dragover') event.dataTransfer.dropEffect = 'none';
		};
		window.addEventListener('dragover', refuseStrayFile);
		window.addEventListener('drop', refuseStrayFile);

		// Отслеживание ширины окна возвращает функцию отписки — раскладка
		// должна переключаться и при повороте телефона, и при ресайзе окна.
		const unwatch = ui.watchViewport();

		// Состояние связи слушается на весь запуск: экраны только читают его.
		const unwatchNetwork = network.watch();

		return () => {
			for (const name of ['gesturestart', 'gesturechange', 'gestureend']) {
				document.removeEventListener(name, stopGesture);
			}
			document.removeEventListener('touchstart', rememberTouch);
			document.removeEventListener('touchmove', stopRubberBand);
			window.removeEventListener('dragover', refuseStrayFile);
			window.removeEventListener('drop', refuseStrayFile);
			unwatch();
			unwatchNetwork();
			stopDesktop();
		};
	});

	/*
	 * О новой версии говорит карточка в списке чатов, а не всплывающая полоска:
	 * полоска уходила через несколько секунд, и установленное приложение
	 * месяцами оставалось старым, ничего об этом не сообщая.
	 *
	 * Служебный сценарий, приготовивший сборку, — второй повод спросить сервер
	 * о версии: так карточка появится и тогда, когда сеть об обновлении ещё
	 * не знала.
	 */
	$effect(() => {
		if (install.updateReady) void install.checkVersion();
	});
</script>

{@render children()}

<!--
	Предложение установить приложение ждёт входа.

	Оно занимает весь экран, и до входа накрывало бы форму входа и страницу
	приглашения — человека, который ещё не завёл учётную запись, встречала бы
	реклама установки вместо регистрации. Отказ по-прежнему запоминается,
	поэтому дважды никто его не увидит.
-->
{#if install.screenOpen && session.phase === 'ready'}
	<InstallScreen />
{/if}

<!-- Уведомления живут в корне: они переживают смену маршрута
     и должны рисоваться поверх любого экрана. -->
<Toaster />
{#if session.phase === 'ready'}<AnnouncementCard />{/if}
<DesktopUpdateBanner />
{#if featureFlags.music}<MusicHost />{/if}

<!--
	Технические работы. В корне, а не внутри мессенджера: экран должен
	накрывать и форму входа, и открытый звонок.
-->
{#if maintenance.blocking && maintenance.state}
	<MaintenanceScreen works={maintenance.state} onfinished={() => maintenance.countdownFinished()} />
{/if}

<!-- Подложка под часами и батареей. Установленное приложение занимает экран
     целиком, и iOS рисует строку состояния белым поверх нашего фона. В тёмной
     теме подложка прозрачна и не видна, в светлой — не даёт белому по белому. -->
<div class="status-bar-scrim" aria-hidden="true"></div>

<style>
	.status-bar-scrim {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		height: var(--safe-top);
		z-index: var(--z-status-bar);
		background: var(--status-bar-scrim);
		pointer-events: none;
	}
</style>
